import React from "react";
import { format } from "lib/globalize-util";
import Button from "components/form/button";
import { useFetchBrandTerms, useUpdateBrandTermsSigned } from "./hooks";
import { TermsAndConditionsEditor, Tabs } from "./terms-and-conditions-editor";
import { $, merge } from "glamor";
import theme from "styles/theme";
import moment from "moment";
import Page from "components/page";
import me from "models/me";
import _ from "lodash";

interface ITermsPageProps {
    brandId: string;
    router: any;
    onSetShowTerms: ({ latest }: { latest: boolean }) => void;
}

export const TermsPage: React.FunctionComponent<ITermsPageProps> = ({ brandId, router, onSetShowTerms }) => {
    const styles = React.useMemo(() => {
        return {
            button: {
                marginRight: "15px",
            },
        };
    }, []);

    const contentStyles = React.useMemo(() => {
        return merge(
            $(" .layoutContainer", {
                padding: "60px 75px 0 75px !important",
            }),
            $(" .topHeader", {
                position: "fixed !important",
                width: "90%",
            }),
            $(" .topHeader", {
                textAlign: "center",
            }),
            $(" .updatedDate", {
                fontSize: "11px",
                color: theme.palette.darkGrey,
            }),
            $(" .termsPrompt", {
                fontSize: "14px",
                padding: "5px 0px",
                color: theme.palette.darkGrey,
            }),
            $(" .actions", {
                textAlign: "right",
                paddingTop: "15px",
            }),
            $(" .confirmation", {
                paddingTop: "15px",
            }),
        );
    }, []);

    const { data: terms } = useFetchBrandTerms(brandId, false);
    const [acceptBtnDisabled, setAcceptBtnDisabled] = React.useState(true);
    const ref = React.useRef<HTMLDivElement>(null);
    const termsRefs = React.useMemo(() => ({ preview: ref }), []);

    React.useLayoutEffect(() => {
        let getRefTimeout;
        if (terms?.body) {
            // delay to fill the ref values
            getRefTimeout = setTimeout(() => {
                setAcceptBtnDisabled(ref?.current?.scrollHeight > ref?.current?.clientHeight);
            });
        }
        return () => {
            if (getRefTimeout) {
                clearTimeout(getRefTimeout);
            }
        };
    }, [terms?.body]);

    const [updateBrandTermsSigned] = useUpdateBrandTermsSigned({
        onSuccess: () => {
            onSetShowTerms({ latest: true });
        },
    });

    const handleClose = React.useCallback(() => {
        router.push("/dashboard");
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSubmit = React.useCallback(() => {
        updateBrandTermsSigned({ userId: me._id, version: terms.version, terms: terms._id });
    }, [terms]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleOnScroll = React.useCallback(
        (event) => {
            if (event && acceptBtnDisabled) {
                setAcceptBtnDisabled(
                    _.toInteger(event.target?.scrollHeight - event.target?.scrollTop) !== event.target?.clientHeight,
                );
            }
        },
        [acceptBtnDisabled],
    );

    return (
        <Page title={format("Terms & Conditions")} className={`${contentStyles}`}>
            <div className="terms-and-conditions-wrapper" onScroll={handleOnScroll}>
                <div className="updatedDate">{`${format("Updated")} ${moment(terms?.modified_on)
                    .local()
                    .format("MMM D, YYYY")}`}</div>
                <div className="termsPrompt">
                    {format("We've updated our terms and conditions. Please read and accept the following:")}
                </div>
                <TermsAndConditionsEditor
                    preview={true}
                    selectedTab={Tabs.Preview}
                    value={terms?.body || ""}
                    refs={termsRefs}
                />
                <div className="confirmation">
                    {format("By clicking I Accept, I affirm that I have read and agree to the conditions above.")}
                </div>
                <div className="actions">
                    <Button key="btn-dismiss" label={format("Cancel")} onClick={handleClose} style={styles.button} />
                    <Button
                        key="btn-save"
                        label={format("I Accept")}
                        onClick={handleSubmit}
                        primary={true}
                        disabled={acceptBtnDisabled}
                    />
                </div>
            </div>
        </Page>
    );
};
